import { useForm } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import type { CaseFolder } from "@/briefpoint-client";
import Button from "@/components/Button";
import PdfViewerModal from "@/components/PdfViewerModal";
import SearchInput from "@/components/SearchInput";
import { useCaseFileApi } from "@/hooks/useApi";
import { FILE_EXPLORER_VARIANTS, SEARCH_PARAMS } from "@/lib/constants";
import type { FileExplorerVariant } from "@/lib/types";
import {
  folderDirectory,
  getCaseId,
  getHistoryPath,
  getSearchParamsVariables,
  updateProductionTabParams,
} from "@/lib/utils";
import styles from "./FileExplorer.module.scss";
import FileExplorerColumn from "./FileExplorerColumn";

interface Props {
  variant: FileExplorerVariant;
}

export default function FileExplorer(props: Props) {
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<CaseFolder[]>([]);

  const urlParams = useParams<{
    id: string;
    caseId: string;
    documentId: string;
    currentStep: string;
    firstFolderId: string;
    secondFolderId: string;
    thirdFolderId: string;
  }>();

  const newBreadcrumbs = [...breadcrumbs];

  function initializeBreadcrumbs(breadcrumbs: CaseFolder[], position: number) {
    /* initialize breadcrumbs on mount */
    if (position === 0) {
      const foundFolder = breadcrumbs.find(
        (breadcrumb) => breadcrumb.id === urlParams.firstFolderId,
      );
      if (foundFolder) {
        newBreadcrumbs[0] = foundFolder;
      }
    }
    if (position === 1) {
      const foundFolder = breadcrumbs.find(
        (breadcrumb) => breadcrumb.id === urlParams.secondFolderId,
      );
      if (foundFolder) {
        newBreadcrumbs[1] = foundFolder;
      }
    }
    if (position === 2) {
      const foundFolder = breadcrumbs.find(
        (breadcrumb) => breadcrumb.id === urlParams.thirdFolderId,
      );
      if (foundFolder) {
        newBreadcrumbs[2] = foundFolder;
      }
    }
    setBreadcrumbs(newBreadcrumbs);
  }

  function updateBreadcrumbs(folder: CaseFolder | null, position: number) {
    if (folder === null) {
      setBreadcrumbs([]);
      return;
    }
    setBreadcrumbs([...breadcrumbs.slice(0, position), folder]);
  }

  const caseFileApi = useCaseFileApi();

  const fileExplorerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const search = searchParams.get(SEARCH_PARAMS.SEARCH);
  const interrogatoryIdParam = Number(
    searchParams.get(SEARCH_PARAMS.INTERROGATORY_ID),
  );
  const filter = searchParams.get(SEARCH_PARAMS.FILTER);
  const caseId = getCaseId(props.variant, urlParams);

  const historyPath = getHistoryPath(
    props.variant,
    urlParams.documentId,
    urlParams.currentStep,
    caseId,
  );

  const searchParamsVariables = getSearchParamsVariables(
    props.variant,
    interrogatoryIdParam,
    filter || "",
    search || "",
  );

  function onClickBreadcrumb(folder: CaseFolder, position: number) {
    updateBreadcrumbs(folder, position);
    setSelectedFile(null);
    history.push({
      pathname: `${historyPath}/${folderDirectory(folder.id, position, urlParams.firstFolderId, urlParams.secondFolderId)}`,
      search: searchParamsVariables,
    });
  }

  function getStyles() {
    if (urlParams.thirdFolderId) {
      return readUrlQuery.data ? styles.threeColumn : "";
    }
    if (urlParams.secondFolderId) {
      if (readUrlQuery.data) {
        return `${styles.twoColumn} ${styles.pdfExtend}`;
      }
      return styles.twoColumn;
    }
    if (urlParams.firstFolderId) {
      return styles.oneColumn;
    }
    return "";
  }

  // QUERY
  const readUrlQuery = useQuery({
    queryKey: ["caseFileApi.caseFileGetReadUrl", selectedFile, caseId],
    enabled: !!selectedFile,
    queryFn: async () => {
      const data = await caseFileApi.caseFileGetReadUrl({
        fileId: selectedFile || "",
        caseId,
      });
      return JSON.parse(data);
    },
  });

  // FORM
  const form = useForm({
    defaultValues: {
      searchText:
        new URLSearchParams(location.search).get(SEARCH_PARAMS.SEARCH) || "",
    },
    listeners: {
      onChangeDebounceMs: 500,
      onChange: ({ formApi }) => {
        // Auto submit when canSubmit is true
        if (formApi.state.canSubmit) {
          void formApi.handleSubmit();
        }
      },
    },
    onSubmit: async () => {
      const navigationParams = updateProductionTabParams(
        location.search,
        {
          tab: SEARCH_PARAMS.PRODUCTION,
          produceSearch: form.state.values.searchText,
          withheldSearch:
            new URLSearchParams(location.search).get(
              SEARCH_PARAMS.WITHHELD_SEARCH,
            ) || undefined,
        },
        caseId,
      );
      // Used for submitting the search text for production files - push used to allow user to navigate in/out of those parameters when set
      history.push(navigationParams);
    },
  });

  /* biome-ignore lint/correctness/useExhaustiveDependencies: run only when search changes */
  useEffect(() => {
    /* need use effect because search comes from outside of component in wizard variant */
    setBreadcrumbs([]);
  }, [search]);

  return (
    <>
      <PdfViewerModal
        url={readUrlQuery.data}
        onClose={() => setSelectedFile(null)}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
      >
        {props.variant === FILE_EXPLORER_VARIANTS.CASE && (
          <div className="pt-3">
            <form.Field
              name="searchText"
              children={(field) => (
                <SearchInput
                  fixed={true}
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  placeholder="Search Documents"
                  size="sm"
                />
              )}
            />
          </div>
        )}
      </form>
      <div className="pt-4">
        {breadcrumbs.length > 0 && (
          <div className={"d-flex mb-2 align-items-center"}>
            {breadcrumbs.map((breadcrumb, idx) => (
              <Button
                key={breadcrumb?.id}
                variant="link"
                onClick={() =>
                  breadcrumbs.length - 1 !== idx &&
                  onClickBreadcrumb(breadcrumb, idx)
                }
                className={`${breadcrumbs.length - 1 !== idx ? styles.breadcrumb : styles.lastBreadcrumb} border-0 p-0 pe-2 text-decoration-none`}
              >
                {breadcrumb?.name}
              </Button>
            ))}
          </div>
        )}
        <div className={styles.container}>
          <div
            ref={fileExplorerRef}
            className={styles.fileExplorer}
            data-testid="file-explorer"
          >
            <div className={`d-flex ${getStyles()}`}>
              <FileExplorerColumn
                variant={props.variant}
                folderId={undefined}
                position={0}
                setSelectedFile={setSelectedFile}
                searchColumn
                selectedFile={selectedFile}
                updateBreadcrumbs={updateBreadcrumbs}
                initializeBreadcrumbs={initializeBreadcrumbs}
              />
              {urlParams.firstFolderId && (
                <FileExplorerColumn
                  variant={props.variant}
                  folderId={urlParams.firstFolderId}
                  position={1}
                  setSelectedFile={setSelectedFile}
                  selectedFile={selectedFile}
                  updateBreadcrumbs={updateBreadcrumbs}
                  initializeBreadcrumbs={initializeBreadcrumbs}
                />
              )}
              {urlParams.secondFolderId && (
                <FileExplorerColumn
                  variant={props.variant}
                  folderId={urlParams.secondFolderId}
                  position={2}
                  setSelectedFile={setSelectedFile}
                  selectedFile={selectedFile}
                  updateBreadcrumbs={updateBreadcrumbs}
                  initializeBreadcrumbs={initializeBreadcrumbs}
                />
              )}
              {urlParams.thirdFolderId && (
                <FileExplorerColumn
                  variant={props.variant}
                  folderId={urlParams.thirdFolderId}
                  position={3}
                  setSelectedFile={setSelectedFile}
                  selectedFile={selectedFile}
                  updateBreadcrumbs={updateBreadcrumbs}
                  initializeBreadcrumbs={initializeBreadcrumbs}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
